import { useQuery } from '@apollo/client';
import React, { useEffect } from 'react';

import { SelectField } from 'components/v2/Form/SelectField/SelectField';
import { Option } from 'components/v2/Inputs/DropdownMenu/DropdownMenu.types';
import { GetDropdownOptionsResponse } from 'kb-shared';
import { GET_DROPDOWN_OPTIONS } from 'kb-shared/graphql/queries';
import { BugTracker } from 'kb-shared/utilities/bugTracker';

import { SelectWithOptionsProps } from './SelectWithOptions.types';

export const SelectWithOptions = (props: SelectWithOptionsProps): JSX.Element => {
  const { onOptionsLoaded, status, helperText, multiSelect = false } = props;
  const { loading, data, error } = useQuery<GetDropdownOptionsResponse>(GET_DROPDOWN_OPTIONS, {
    variables: { key: props.lookup_key },
    onError: error => {
      BugTracker.notify(error, `SelectWithOptions: ${props.lookup_key}`);
    }
  });

  useEffect(() => {
    if (loading) return;
    if (error) onOptionsLoaded?.(false);
    if (data && data.getDropdownOptions?.length > 0) onOptionsLoaded?.(true);
    else onOptionsLoaded?.(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, error, loading]);

  const allOptions = data?.getDropdownOptions || [];

  const getSelectedOptions = () => {
    return allOptions.filter(option => props.valueList.includes(option.value));
  };

  const selectedOptions = getSelectedOptions();

  const handleSelectChange = (option: Option) => {
    if (multiSelect) {
      const currentValues = props.valueList;
      const newValues = currentValues.includes(option.value)
        ? currentValues.filter(v => v !== option.value)
        : [...currentValues, option.value];

      props.onSelect?.(newValues);
    } else {
      props.onSelect?.([option.value]);
    }
  };

  return (
    <SelectField
      id={props.inputId || ''}
      onChange={handleSelectChange}
      options={allOptions}
      label={props.label}
      selected={selectedOptions}
      placeholder={props.placeholder}
      status={status}
      helperText={helperText}
      multiSelect={multiSelect}
    />
  );
};
